import { TierIiReportStep } from "generated-graphql/graphql";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReportStepState, reportStepMetadata } from "util/reportStepMetadata";

import { useReportSteps } from "./useReport";

type UseNavigateToNextStepParams = {
  /** Optional override of the current step if not on a step route */
  currentStep?: TierIiReportStep;
};

/**
 * Navigates to the next non-completed step (NotStarted or Invalid) after the given current step.
 * If all steps (including Review) are complete with no compliance issues, navigates to Verify & Submit.
 */
export function useNavigateToNextStep({ currentStep }: UseNavigateToNextStepParams = {}) {
  const { tenantId = "", reportId = "" } = useParams<{
    tenantId: string;
    reportId: string;
  }>();
  const navigate = useNavigate();

  // Call useReportSteps at the top level to get a ref to the latest steps
  // This will re-render when Apollo cache updates
  const steps = useReportSteps();

  const navigateToNext = useCallback(
    (fallbackToOverview = true, providedCurrentStep?: TierIiReportStep) => {
      const activeCurrentStep = providedCurrentStep ?? currentStep;

      // Use the steps from the hook closure - but this closure will be recreated
      // when steps changes, so we'll always have fresh data
      const orderedSteps = Object.keys(steps) as TierIiReportStep[];
      const contentSteps = orderedSteps.filter(
        (s) => s !== TierIiReportStep.ReportOverview && s !== TierIiReportStep.VerifyAndSubmit
      );

      // Determine the index of the current step in the ordered list (content steps only)
      const currentIndex = activeCurrentStep
        ? contentSteps.findIndex((s) => s === activeCurrentStep)
        : -1;

      // 1) Forward scan: next non-completed step after the current step
      for (let i = currentIndex + 1; i < contentSteps.length; i++) {
        const step = contentSteps[i];
        const state = steps[step];
        if (!state) continue;
        if (state.status !== ReportStepState.Done) {
          const route = reportStepMetadata[step].route;
          const path = route
            ? `/o/${tenantId}/chemicals/reports/${reportId}/${route}`
            : `/o/${tenantId}/chemicals/reports/${reportId}`;
          navigate(path);
          return;
        }
      }

      // 2) If none forward, but there exists an unresolved step anywhere (e.g. earlier), route to the first unresolved step
      const firstUnresolved = contentSteps.find((s) => steps[s]?.status !== ReportStepState.Done);
      if (firstUnresolved) {
        const route = reportStepMetadata[firstUnresolved].route;
        const path = route
          ? `/o/${tenantId}/chemicals/reports/${reportId}/${route}`
          : `/o/${tenantId}/chemicals/reports/${reportId}`;
        navigate(path);
        return;
      }

      // 3) All content steps are done (no unresolved). Navigate to Verify & Submit if present
      const hasVerify = orderedSteps.includes(TierIiReportStep.VerifyAndSubmit);
      if (hasVerify) {
        const verifyRoute = reportStepMetadata[TierIiReportStep.VerifyAndSubmit].route;
        const verifyPath = verifyRoute
          ? `/o/${tenantId}/chemicals/reports/${reportId}/${verifyRoute}`
          : `/o/${tenantId}/chemicals/reports/${reportId}`;
        navigate(verifyPath);
        return;
      }

      // Fallback to Overview if requested
      if (fallbackToOverview) {
        navigate(`/o/${tenantId}/chemicals/reports/${reportId}`);
      }
    },
    [currentStep, steps, navigate, reportId, tenantId]
  );

  return navigateToNext;
}
